import { zodResolver } from '@hookform/resolvers/zod';
import { type Person, personNameSchema } from '@familywallet/shared';
import { useMutation } from '@tanstack/react-query';
import { ChevronRight } from 'lucide-react';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router';
import { z } from 'zod';
import { FormError } from '../components/FormError';
import { PageHeader } from '../components/PageHeader';
import { Button } from '../components/ui/button';
import { Card, IconSquare, ListGroup, listRowClass } from '../components/ui/card';
import { Field } from '../components/ui/field';
import { Input } from '../components/ui/input';
import { api } from '../lib/api';
import { useCan } from '../lib/auth';
import { usePeople } from '../lib/ledger';
import { useInvalidateLedger } from '../lib/ledger-mutations';
import { useIdempotencyKey } from '../lib/members';
import { cn } from '../lib/utils';

/** Name, phone and note as typed; empty phone or note means none. */
export const personForm = z.object({
  name: personNameSchema,
  phone: z.string().trim().max(40),
  note: z.string().trim().max(500),
});
export type PersonFormInput = z.input<typeof personForm>;
export type PersonFormOutput = z.output<typeof personForm>;

function PersonRow({ person }: { person: Person }) {
  const { t } = useTranslation();
  return (
    <Link to={`/people/${person.id}`} className={listRowClass}>
      <IconSquare>{person.name.slice(0, 1).toUpperCase()}</IconSquare>
      <span className="flex min-w-0 flex-1 flex-col">
        <span
          className={cn(
            'truncate text-[15px] font-medium',
            person.archivedAt && 'text-muted line-through',
          )}
        >
          {person.name}
        </span>
        <span className="truncate text-xs text-muted">
          {person.archivedAt
            ? t('people.archived')
            : person.debtCount > 0
              ? t('people.debts', { count: person.debtCount })
              : t('people.noDebts')}
        </span>
      </span>
      <ChevronRight aria-hidden="true" className="size-5 shrink-0 text-muted rtl:rotate-180" />
    </Link>
  );
}

function AddPerson() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const invalidate = useInvalidateLedger();
  const [idempotencyKey, renewKey] = useIdempotencyKey();
  const form = useForm<PersonFormInput, unknown, PersonFormOutput>({
    resolver: zodResolver(personForm),
    defaultValues: { name: '', phone: '', note: '' },
  });
  const add = useMutation({
    mutationFn: (v: PersonFormOutput) =>
      api.people.create(
        {
          name: v.name,
          ...(v.phone ? { phone: v.phone } : {}),
          ...(v.note ? { note: v.note } : {}),
        },
        { idempotencyKey },
      ),
    onSuccess: async (person) => {
      renewKey();
      await invalidate();
      navigate(`/people/${person.id}`);
    },
  });
  const errors = form.formState.errors;
  return (
    <Card>
      <form
        noValidate
        className="flex flex-col gap-3"
        onSubmit={form.handleSubmit((v) => add.mutate(v))}
      >
        <FormError error={add.error} />
        <Field label={t('people.name')} error={errors.name}>
          <Input autoComplete="off" {...form.register('name')} />
        </Field>
        <Field label={t('people.phone')} hint={t('people.optional')} error={errors.phone}>
          <Input type="tel" autoComplete="off" dir="ltr" {...form.register('phone')} />
        </Field>
        <Field label={t('people.note')} hint={t('people.optional')} error={errors.note}>
          <Input autoComplete="off" {...form.register('note')} />
        </Field>
        <Button type="submit" disabled={add.isPending}>
          {add.isPending ? t('common.saving') : t('people.add')}
        </Button>
      </form>
    </Card>
  );
}

/** People outside the family: search, open one, add one. */
export function PeoplePage() {
  const { t } = useTranslation();
  const outside = useCan('debts.outside');
  const manage = useCan('people.manage');
  const [search, setSearch] = useState('');
  const [showArchived, setShowArchived] = useState(false);
  const [adding, setAdding] = useState(false);
  const people = usePeople(search.trim(), showArchived);
  const items = people.data?.items ?? [];
  const canAdd = outside || manage;

  return (
    <div className="flex flex-col gap-4">
      <PageHeader
        title={t('people.title')}
        back="/debts"
        action={
          canAdd && !adding ? (
            <Button variant="small" onClick={() => setAdding(true)}>
              {t('people.add')}
            </Button>
          ) : undefined
        }
      />
      <p className="px-1 text-sm text-muted">{t('people.hint')}</p>
      {adding && <AddPerson />}
      <Field label={t('people.search')}>
        <Input type="search" value={search} onChange={(e) => setSearch(e.target.value)} />
      </Field>
      {people.isError && <FormError error={people.error} />}
      {people.isPending ? (
        <p role="status">{t('app.loading')}</p>
      ) : items.length === 0 ? (
        <p className="px-1 text-muted">{t('people.empty')}</p>
      ) : (
        <ListGroup>
          {items.map((p) => (
            <li key={p.id}>
              <PersonRow person={p} />
            </li>
          ))}
        </ListGroup>
      )}
      <Button variant="ghost" onClick={() => setShowArchived((v) => !v)}>
        {t('people.showArchived')}
        {showArchived ? ' ✓' : ''}
      </Button>
    </div>
  );
}
